import type {
  DeliverableTypeOption,
  DeliverableView,
  ProjectView,
  ReferenceOption,
  TaskView,
} from "@fieldpath/shared";
import { holdsContainerOpen, isPendingRequest, mdy, mdyDay } from "@fieldpath/shared";
import { useEffect, useState } from "react";

import { api, ApiError, type Fetcher } from "../api/client";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { rowOverdue } from "../overdue";
import { navigate } from "../router";
import { HelpButton } from "./HelpButton";
import { DateHint, PAST_DATE_BOX, todayLocal } from "./DateHint";
import { CancelToggle, CompleteToggle } from "./CancelToggle";
import { CancelledTasks } from "./CancelledTasks";
import { EditProject } from "./EditProject";
import { Modal } from "./Modal";
import { AddFromTemplates } from "./AddFromTemplates";
import { NewTask } from "./NewTask";
import { ProjectGrid } from "./ProjectGrid";
import { SubjectFields } from "./FieldsPanel";
import { PeoplePicker } from "./PeoplePicker";
import { KindTag } from "./KindTag";
import { EditChip } from "./EditChip";
import { useTaskHover } from "./TaskHoverCard";
import type { TaskHoverBind } from "./TaskHoverCard";

interface Props {
  fetcher: Fetcher;
  project: ProjectView;
  onBack: () => void;
  onOpenTask: (task: TaskView) => void;
  /** Re-fetch the project itself, after it is closed or reopened. */
  onChanged: () => void;
}

export function ProjectDetail({
  fetcher,
  project,
  onBack,
  onOpenTask,
  onChanged,
}: Props) {
  // One hook per screen: `bind` goes on each task row, `card` renders the
  // portal. The cache inside it is module-level, so the deliverable cards and
  // the ungrouped list share it rather than each fetching the same task.
  const { bind, card } = useTaskHover(fetcher);
  // Closed either way: nothing more can be added.
  const closed = project.cancelledAt !== null || project.completedAt !== null;
  const [tasks, setTasks] = useState<TaskView[] | null>(null);
  const [deliverables, setDeliverables] = useState<DeliverableView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [addingDeliverable, setAddingDeliverable] = useState(false);
  const [addingFromTemplates, setAddingFromTemplates] = useState(false);
  // The grid is a second way to look at the same work (E2 5b). Not
  // remembered: the list is what a project page is for.
  const [showGrid, setShowGrid] = useState(false);
  const [editing, setEditing] = useState(false);
  // null until the check resolves. Starting at false told a PM they were
  // not one for as long as two requests took, and for ever if either
  // failed — a claim about somebody's permissions should not be a guess
  // made before asking.
  const [isPm, setIsPm] = useState<boolean | null>(null);
  // Taken from the `api.me` call this screen already makes, rather than a
  // second request for a fact the first one carries.
  const [meKey, setMeKey] = useState<string | null>(null);
  // Admins manage every project (cancel, reinstate, PM/TM). Unknown is null,
  // which offers nothing -- the server decides either way.
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  useEffect(() => {
    void api.amIAdmin(fetcher).then(setIsAdmin).catch(() => setIsAdmin(null));
  }, [fetcher]);
  /**
   * May this person manage the project as a whole: its PM or an admin
   * (`mayManageProject` on the server, 12 Oct 2026). Mirrors it so Cancel and
   * Reinstate are only offered to somebody the server will let use them.
   */
  const mayManage = (meKey !== null && meKey === project.pmKey) || isAdmin === true;
  // Naming the PM and TM of a project that has no PM is open to any PM
  // (punch list #24, `mayNameManagers`); everything else on Edit project is
  // `mayManage`'s since 22 Oct 2026. Somebody with neither is not offered it.
  const mayNameManagers = mayManage || (project.pmKey === null && isPm === true);

  // Only a PM may close anything. The SERVER still decides — this governs
  // whether the action is offered, so a wrong answer hides a button rather
  // than granting anything.
  useEffect(() => {
    void Promise.all([api.me(fetcher), api.projectManagers(fetcher)])
      .then(([me, pms]) => {
        setMeKey(me.employee?.ajeraKey ?? null);
        setIsPm(
          Boolean(me.employee && pms.some((p) => p.key === me.employee!.ajeraKey)),
        );
      })
      // Unknown, not "no": the check failing is not evidence about anyone.
      .catch(() => {
        setIsPm(null);
        setMeKey(null);
      });
  }, [fetcher]);

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  const reload = () =>
    api
      .listProjectWork(fetcher, project.id)
      .then((r) => {
        setTasks(r.tasks);
        setDeliverables(r.deliverables);
      })
      .catch((e: Error) => setError(e.message));

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher, project.id, back]);

  // Grouped by deliverable, with everything else after. Ungrouped is a normal
  // state, not a leftover, so it gets its own heading rather than being
  // bolted onto the end of the last group.
  // Cancelled tasks leave the deliverable cards and the loose list for a
  // section of their own (punch list #28).
  const live = (tasks ?? []).filter((t) => !t.cancelledAt);
  const cancelled = (tasks ?? []).filter((t) => t.cancelledAt);
  // Cancelled DELIVERABLES go there too (punch list #32/#34, 20 Oct 2026):
  // the section took only tasks, so a cancelled deliverable kept its card
  // among the live work. It can no longer take new work, so it is not offered
  // to New task either.
  const liveDeliverables = deliverables.filter((d) => !d.cancelledAt);
  const cancelledDeliverables = deliverables.filter((d) => d.cancelledAt);
  const grouped = liveDeliverables.map((d) => ({
    deliverable: d,
    tasks: live.filter((t) => t.deliverableId === d.id),
  }));
  const ungrouped = live.filter((t) => !t.deliverableId);

  // Eligibility, mirroring the server so a button is only offered when it
  // would actually work. Cancel needs the project EMPTY; complete needs
  // every task Done and a PM.
  // Cancelled and hibernated work does not hold the project open, as on the
  // server: `holdsContainerOpen` is its rule (hibernated since 23 Oct 2026).
  const openTasks = live.filter(holdsContainerOpen).length;
  const isEmpty = (tasks ?? []).length === 0 && deliverables.length === 0;
  const canCancel = !closed && tasks !== null && isEmpty && mayManage;
  const canComplete =
    !closed && tasks !== null && (isPm === true || isAdmin === true) && openTasks === 0;

  return (
    <>
      {card}
      <button className="linkish" onClick={onBack} style={{ marginBottom: 12 }}>
        ← All projects
      </button>

      <div
        className={`card is-project row spread${
          // Punch list #77: done and cancelled look different from each other,
          // not only from open -- the banner said which; now the card does too.
          project.completedAt ? " closed done" : project.cancelledAt ? " closed cancelled" : ""
        }`}
        style={{ marginBottom: closed ? 10 : 18 }}
      >
        <div>
          <h1 className="project-h" style={{ marginTop: 0, marginBottom: 4 }}>
            <KindTag kind="project" size="head" />
            {project.name}
            {/* The EDIT chip sits against the name it edits. It used to be a
                button in the group on the right, beside "New deliverable" and
                "New task" -- so that group mixed CHANGE THIS with MAKE
                ANOTHER, which is how people pressed the wrong one. A closed
                project cannot be edited, so it is not offered. */}
            {closed || !mayNameManagers || project.isPersonal ? null : (
              <EditChip
                title={editing ? "Stop editing this project" : "Edit this project"}
                pressed={editing}
                onClick={() => setEditing((e) => !e)}
              />
            )}
            <HelpButton screens="project" />
          </h1>
          <p className="muted" style={{ margin: 0 }}>
            <span className="chip">{project.projectType}</span>{" "}
            {project.clientName ? (
              <span className="client-name">{project.clientName}</span>
            ) : (
              // "No client" is a STATE, not a name, so it stays upright --
              // italic would say there is a client called "No client".
              "No client"
            )}
            {project.deltekProjectName ? ` · Ajera: ${project.deltekProjectName}` : ""}
            {/* Display only (E9.3) -- these grant nothing. Said only when
                set: "PM: none" on every older project would read as a
                problem when it is simply not filled in yet. */}
            {project.pmKey ? ` · PM: ${project.pmName ?? project.pmKey}` : ""}
            {project.tmKey ? ` · TM: ${project.tmName ?? project.tmKey}` : ""}
            {/* Punch list #76: when it was created -- enough to tell this
                year's job from last year's of the same name. The reader's own
                calendar, as for every time on screen; MM/DD/YYYY (F193). */}
            {` · Created ${mdyDay(project.createdAt)}`}
          </p>
          {/* Where the EDIT chip went, once we know who is asking. */}
          {!closed && !mayNameManagers && isAdmin === false && isPm !== null ? (
            <p className="muted" style={{ margin: "4px 0 0", fontSize: 13 }}>
              Only this project's PM or an admin can edit it.
            </p>
          ) : null}
        </div>
        {/* A closed project offers exactly one action: undo. Leaving the
            others visible but greyed invites people to try them and wonder
            why nothing happens, which is the same complaint in a different
            costume. */}
        <div className="row" style={{ justifyContent: "flex-end" }}>
          {closed ? (
            project.completedAt ? (
              <CompleteToggle
                what="project"
                completedAt={project.completedAt}
                onToggle={async (next) => {
                  await api.setProjectCompleted(fetcher, project.id, next);
                  onChanged();
                }}
              />
            ) : mayManage ? (
              // Reinstating is the PM's or an admin's, like cancelling.
              <CancelToggle
                what="project"
                cancelledAt={project.cancelledAt}
                onToggle={async (next) => {
                  await api.setProjectCancelled(fetcher, project.id, next);
                  onChanged();
                }}
              />
            ) : null
          ) : (
            <>
              {canComplete ? (
                <CompleteToggle
                  what="project"
                  completedAt={null}
                  onToggle={async (next) => {
                    await api.setProjectCompleted(fetcher, project.id, next);
                    onChanged();
                  }}
                />
              ) : null}
              {canCancel ? (
                <CancelToggle
                  what="project"
                  cancelledAt={null}
                  onToggle={async (next) => {
                    await api.setProjectCancelled(fetcher, project.id, next);
                    onChanged();
                  }}
                />
              ) : null}
              {/* A personal project (F171) holds personal tasks only, made
                  from My work: none of these is offered there. */}
              {project.isPersonal ? null : (
                <>
                  {/* Only CREATE actions here now. Each carries its kind tag,
                      because these two sit side by side and get pressed in a
                      hurry -- the tag is read before the words are. */}
                  {/* Most work arrives this way once templates exist: a
                      deliverable and its tasks in one go (E2 4b). */}
                  <button
                    className="create"
                    onClick={() => setAddingFromTemplates((a) => !a)}
                  >
                    {addingFromTemplates ? "Cancel" : "From templates"}
                  </button>
                  {/* On an empty project the deliverable is the filled button:
                      that is where work usually starts (punch list #47). */}
                  <button
                    className={tasks !== null && isEmpty ? "primary" : "create"}
                    onClick={() => setAddingDeliverable((a) => !a)}
                  >
                    {addingDeliverable ? (
                      "Cancel"
                    ) : (
                      <>
                        <KindTag kind="deliverable" size="row" />
                        New deliverable
                      </>
                    )}
                  </button>
                  <button className={tasks !== null && isEmpty ? "create" : "primary"} onClick={() => setAdding((a) => !a)}>
                    {adding ? (
                      "Cancel"
                    ) : (
                      <>
                        <KindTag kind="task" size="row" />
                        New task
                      </>
                    )}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Why closing is not on offer. Hiding a button without saying why
          leaves a PM hunting for a feature that is right there and simply
          not applicable yet. */}
      {!closed && tasks !== null && !canComplete && !canCancel ? (
        <p className="muted" style={{ marginTop: -8, marginBottom: 16 }}>
          {openTasks > 0
            ? `${openTasks} task${openTasks === 1 ? "" : "s"} still open — this project can be marked complete once they are all done.`
            : isPm === false && isAdmin === false
              ? "Only a project manager or an admin can close a project."
              : "Checking whether you can close this…"}
        </p>
      ) : null}

      {/* Said once, loudly, above everything. A state that changes what the
          whole page can do should not be inferable only from a chip. */}
      {closed ? (
        <div className={project.completedAt ? "closed-banner done" : "closed-banner"}>
          <strong>
            This project is {project.completedAt ? "completed" : "cancelled"}.
          </strong>{" "}
          {project.completedAt
            ? `Closed ${mdyDay(project.completedAt)}. No new deliverables or tasks can be added.`
            : `Cancelled ${mdyDay(project.cancelledAt!)}. No new deliverables or tasks can be added.`}{" "}
          Reopen it to make changes.
        </div>
      ) : null}

      {/* The template project (21 Oct 2026): said as loudly as closed, because
          it changes who can see the page and where its work appears. */}
      {project.isTemplate ? (
        <div className="closed-banner template">
          <strong>This is the template project.</strong> Templates are built here and read back
          into "Add from templates". Only the people on Admin › Settings can see it, its work is
          off My work, Workload, Overview and Needs attention, and it notifies nobody.
        </div>
      ) : null}

      {error ? <div className="error">{error}</div> : null}

      {/* A pop-up rather than a form opening in the page (punch list #3,
          12 Oct 2026): editing the project is a question to answer, not more
          of the page to scroll past. */}
      {editing && !closed ? (
        <Modal title="Edit project" onCancel={() => setEditing(false)}>
          <EditProject
            fetcher={fetcher}
            project={project}
            inDialog
            // While the project has no PM any PM may name one (punch list
            // #24, `mayNameManagers` on the server).
            mayChangeManagers={mayNameManagers}
            mayChangeDetails={mayManage}
            onSaved={() => {
              setEditing(false);
              onChanged();
            }}
            onCancel={() => setEditing(false)}
          />
        </Modal>
      ) : null}

      {addingFromTemplates ? (
        <AddFromTemplates
          fetcher={fetcher}
          projectId={project.id}
          onAdded={() => void reload()}
          onDone={() => setAddingFromTemplates(false)}
        />
      ) : null}

      {addingDeliverable ? (
        <NewDeliverable
          fetcher={fetcher}
          projectId={project.id}
          onCreated={() => {
            setAddingDeliverable(false);
            void reload();
          }}
        />
      ) : null}

      {adding ? (
        <NewTask
          fetcher={fetcher}
          projectId={project.id}
          projectPmKey={project.pmKey}
          deliverables={liveDeliverables}
          onCreated={() => {
            setAdding(false);
            void reload();
          }}
        />
      ) : null}

      {/* The project's own custom fields (E3/4). Nothing when it has none. */}
      <SubjectFields
        fetcher={fetcher}
        subject={{ level: "project", id: project.id }}
        title="Project details"
        locked
      />

      {tasks !== null && tasks.length > 0 ? (
        <div className="view-switch" role="group" aria-label="How to show the work">
          <button className={showGrid ? "" : "on"} onClick={() => setShowGrid(false)}>
            List
          </button>
          <button className={showGrid ? "on" : ""} onClick={() => setShowGrid(true)}>
            Grid
          </button>
        </div>
      ) : null}
      {/* The grid's own ? (P6), beside the switch that opened it. */}
      {showGrid && tasks !== null && tasks.length > 0 ? (
        <HelpButton screens={["grid", "project"]} label="Help for the grid" />
      ) : null}

      {showGrid ? (
        <ProjectGrid fetcher={fetcher} projectId={project.id} />
      ) : tasks === null ? (
        <p className="muted">Loading…</p>
      ) : (
        <div className="proj-wrap">
          <div className="proj-main">
            {grouped.map(({ deliverable, tasks: within }) => (
              <DeliverableCard
                key={deliverable.id}
                deliverable={deliverable}
                tasks={within}
                onOpenTask={onOpenTask}
                hover={bind}
                meKey={meKey}
              />
            ))}

            <section className="loose-group">
              <div className="section-label">
                {/* With no deliverables yet this list is ALL the work, so it
                    says so rather than "Tasks", which read as "start with a
                    task" (punch list #47, Jen, 20 Oct 2026). */}
                <span>{grouped.length > 0 ? "Not part of a deliverable" : "Deliverables and tasks"}</span>
                <span className="count">{ungrouped.length}</span>
              </div>
              {ungrouped.length === 0 ? (
                <p className="muted" style={{ padding: "12px 0" }}>
                  {live.length === 0
                    ? "Nothing here yet. Start with a deliverable, or a task on its own."
                    : "Everything is grouped."}
                </p>
              ) : (
                <TaskTable
                  tasks={ungrouped}
                  onOpenTask={onOpenTask}
                  hover={bind}
                  meKey={meKey}
                />
              )}
            </section>

            <CancelledTasks
              tasks={cancelled}
              deliverables={cancelledDeliverables}
              deliverableNames={new Map(deliverables.map((d) => [d.id, d.name]))}
            />
          </div>

          <AtAGlance tasks={tasks} deliverables={liveDeliverables} meKey={meKey} />
        </div>
      )}
    </>
  );
}

/**
 * One deliverable and the tasks beneath it.
 *
 * The client date sits in the header because it is the commitment; the task
 * dates in the rows are internal working dates. Showing both without saying
 * which is which is how people end up reassuring a client using a date that
 * was never promised to them.
 */
function DeliverableCard({
  deliverable,
  tasks,
  onOpenTask,
  hover,
  meKey,
}: {
  deliverable: DeliverableView;
  tasks: TaskView[];
  onOpenTask: (task: TaskView) => void;
  hover: TaskHoverBind;
  /** The signed-in person's Ajera KEY. Never matched on a display name. */
  meKey: string | null;
}) {
  // Collapsed is per-card and not remembered. A project page is read top to
  // bottom; a deliverable that stayed shut from last week would hide work
  // without saying it had.
  const [open, setOpen] = useState(true);
  const done = tasks.filter((t) => t.status === "Done").length;

  return (
    /*
     * ONE CARD that wraps its tasks, replacing a light group box, a bordered
     * header card and an indented list.
     *
     * This inverts a rule written in CLAUDE.md -- "line weight climbs as you
     * go IN, because the innermost box is the thing you actually click" -- and
     * the inversion is deliberate. The REASON behind that rule was to make the
     * clickable thing findable, and here that job is done by a 52px row, a
     * full-width hover wash and a TSK tag leading every line. Border weight is
     * no longer carrying it, so the card is free to be the strong edge and
     * say "these tasks are one deliverable" without indentation.
     */
    <div className="dcard">
      <div className="dcard-head">
        <div className="dcard-head-main">
          <button
            className="dcard-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
          >
            <span className="caret" aria-hidden="true">{open ? "▾" : "▸"}</span>
            <KindTag kind="deliverable" size="head" />
            <span className="dcard-name">{deliverable.name}</span>
          </button>
          <button
            className="open-deliverable"
            onClick={() => navigate({ kind: "deliverable", id: deliverable.id })}
          >
            OPEN DELIVERABLE <span aria-hidden="true">›</span>
          </button>

          <div className="dcard-meta">
            {deliverable.typeName ? (
              <span className="chip">{deliverable.typeName}</span>
            ) : null}
            <span
              className={`state ${deliverable.status.toLowerCase().replace(" ", "_")}`}
            >
              {deliverable.status}
            </span>
            {isPendingRequest(deliverable) ? <span className="chip requested">Requested</span> : null}
            {/* Saying WHERE the status comes from matters: otherwise a
                deliverable reading Done with two tasks still open looks like
                a bug rather than a decision somebody made. */}
            <span className="muted" style={{ fontSize: 12 }}>
              {deliverable.gatingTaskTitle
                ? `follows “${deliverable.gatingTaskTitle}”`
                : "from all tasks"}
            </span>
          </div>
        </div>

        {/* The client date is the COMMITMENT; the dates in the rows are
            internal working dates. Showing both without saying which is which
            is how people reassure a client using a date nobody promised. */}
        <div className="due-box">
          <div className="due-box-label">DUE TO CLIENT</div>
          <div className="due-box-date">{deliverable.clientDueDate ? mdy(deliverable.clientDueDate) : "not set"}</div>
        </div>
      </div>

      {/* One segment per task. A count says "3 of 7"; this says WHICH seven,
          and a row of grey with one red segment reads before any number. */}
      {tasks.length > 0 ? (
        <div className="progress-strip" aria-hidden="true">
          {tasks.map((t) => (
            <span
              key={t.id}
              className={`seg seg-${t.status.toLowerCase().replace(" ", "_")}`}
            />
          ))}
        </div>
      ) : null}

      {deliverable.tasksPastClientDate > 0 ? (
        <div className="past-client-bar">
          <span className="marker" aria-hidden="true" />
          {deliverable.tasksPastClientDate === 1
            ? "1 task is scheduled to finish after the client due date."
            : `${deliverable.tasksPastClientDate} tasks are scheduled to finish after the client due date.`}
        </div>
      ) : null}

      {open ? (
        tasks.length === 0 ? (
          <p className="muted" style={{ padding: "14px 20px" }}>
            Nothing in this deliverable yet. Open a task and set “Part of”.
          </p>
        ) : (
          <TaskTable
            tasks={tasks}
            clientDueDate={deliverable.clientDueDate}
            gatingTaskId={deliverable.gatingTaskId}
            onOpenTask={onOpenTask}
            hover={hover}
            meKey={meKey}
          />
        )
      ) : null}

      <div className="dcard-foot">
        <span className="foot-summary">
          {tasks.length} task{tasks.length === 1 ? "" : "s"} · {done} done
          {deliverable.tasksPastClientDate > 0
            ? ` · ${deliverable.tasksPastClientDate} past client date`
            : ""}
        </span>
      </div>
    </div>
  );
}

/**
 * The project at a glance: four numbers, the client dates, and who is on it.
 *
 * Everything here is DERIVED from what the page already loaded. No new
 * request, and nothing it shows is a fact the rows below do not also carry --
 * the panel's whole job is saving somebody from counting.
 */
function AtAGlance({
  tasks,
  deliverables,
  meKey,
}: {
  tasks: TaskView[];
  deliverables: DeliverableView[];
  /** The signed-in person's Ajera KEY. Never matched on a display name. */
  meKey: string | null;
}) {
  // Remembered per person, because a panel you close should stay closed --
  // and in localStorage rather than on the server, since it is a preference
  // about one browser and not a fact about the project.
  const [minimized, setMinimized] = useState(() => {
    try {
      return window.localStorage.getItem("fp.glance") === "min";
    } catch {
      // Private windows and blocked site data throw here. A panel that cannot
      // remember its state is not a reason to fail the page.
      return false;
    }
  });

  const set = (next: boolean): void => {
    setMinimized(next);
    try {
      window.localStorage.setItem("fp.glance", next ? "min" : "open");
    } catch {
      /* Not remembering is the whole cost. */
    }
  };

  const open = tasks.filter((t) => t.status !== "Done" && !t.cancelledAt).length;
  const blocked = tasks.filter((t) => t.status === "Blocked").length;
  const done = tasks.filter((t) => t.status === "Done").length;
  const atRisk = deliverables.reduce((n, d) => n + d.tasksPastClientDate, 0);

  /*
   * Distinct people, with how much each is holding. ROLE is not shown: the
   * payload carries who a task is assigned to and not what they are to the
   * project, and inventing "PM" from an assignment would be a guess on screen.
   *
   * **Keyed by the Ajera KEY, not the name** -- 5 Oct 2026. It was a
   * `Map<name, count>`, so two people with the same display name merged into
   * one row showing their combined total, with nothing saying so. The key is
   * the identity; the name is the label.
   */
  const people = new Map<string, { name: string; count: number }>();
  for (const t of tasks) {
    if (t.assigneeKey && t.assigneeName && !t.cancelledAt) {
      const seen = people.get(t.assigneeKey);
      people.set(t.assigneeKey, {
        name: t.assigneeName,
        count: (seen?.count ?? 0) + 1,
      });
    }
  }

  if (minimized) {
    return (
      <button className="glance-tab" onClick={() => set(false)} title="Show at a glance">
        <span className="glance-tab-text">AT A GLANCE</span>
        <span aria-hidden="true">‹</span>
        {blocked > 0 ? <span className="glance-tab-blocked">{blocked}</span> : null}
      </button>
    );
  }

  return (
    <aside className="glance">
      <div className="section-label">
        <span>At a glance</span>
        <button className="edit-chip" onClick={() => set(true)} title="Minimize this panel">
          MINIMIZE <span aria-hidden="true">›</span>
        </button>
      </div>

      <div className="glance-grid">
        <Stat value={open} label="Open tasks" />
        <Stat value={blocked} label="Blocked" tone={blocked > 0 ? "late" : undefined} />
        <Stat value={done} label="Done" tone="ok" />
        <Stat value={atRisk} label="Client date at risk" tone={atRisk > 0 ? "soon" : undefined} />
      </div>

      {deliverables.length > 0 ? (
        <>
          <div className="section-label">
            <span>Client dates</span>
          </div>
          {deliverables.map((d) => (
            <div key={d.id} className="glance-row">
              <span className="glance-row-main">
                <KindTag kind="deliverable" size="crumb" />
                <span className="deliv-name">{d.name}</span>
              </span>
              <span className="glance-date">{d.clientDueDate ? mdy(d.clientDueDate) : "—"}</span>
            </div>
          ))}
        </>
      ) : null}

      {people.size > 0 ? (
        <>
          <div className="section-label">
            <span>People</span>
          </div>
          {[...people.entries()].map(([key, p]) => (
            <div key={key} className="glance-row">
              <span className="glance-row-main">
                <span className="avatar" aria-hidden="true">{initials(p.name)}</span>
                <span className={key === meKey ? "mine-name" : undefined}>
                  {p.name}
                </span>
              </span>
              <span className="glance-date">
                {p.count} task{p.count === 1 ? "" : "s"}
              </span>
            </div>
          ))}
        </>
      ) : null}
    </aside>
  );
}

function Stat({
  value,
  label,
  tone,
}: {
  value: number;
  label: string;
  tone?: "late" | "ok" | "soon";
}) {
  return (
    <div className="stat">
      <div className={`stat-value${tone ? ` tone-${tone}` : ""}`}>{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

/** The first letters of a name, for the small round avatar. */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  return (words[0]![0]! + (words[1]?.[0] ?? "")).toUpperCase();
}

/**
 * A list of tasks, and nothing else.
 *
 * Grouping used to be a dropdown on every row, which turned the project screen
 * into a control panel when its job is to show where the work stands. It is
 * now set from inside the task, where the person has the context to decide.
 */
function TaskTable({
  tasks,
  clientDueDate,
  gatingTaskId,
  onOpenTask,
  hover,
  meKey,
}: {
  tasks: TaskView[];
  clientDueDate?: string | null;
  gatingTaskId?: string | null;
  onOpenTask: (task: TaskView) => void;
  hover: TaskHoverBind;
  /** The signed-in person's Ajera KEY. Never matched on a display name. */
  meKey: string | null;
}) {
  /*
   * ROWS, not boxes. The previous note here argued the other way -- "giving
   * each task its own boundary inside the deliverable's is what makes the
   * nesting readable; a table renders them as one undifferentiated block" --
   * and that was right while the deliverable was a header with an indented
   * list under it.
   *
   * Inside a card that already encloses them, a border per row is a third
   * boundary saying what the card's edge and the indent already say. What
   * stops them reading as an undifferentiated block now is the row height,
   * the hover wash and the tag leading each line, none of which the old
   * layout had.
   */
  const today = todayLocal();
  return (
    <>
      {tasks.map((t) => {
        // Two different facts, kept apart so they never look alike (punch
        // list F150): a date that has PASSED, and a plan that already misses
        // the client's date -- a planning warning, not lateness.
        const overdue = rowOverdue(t, today);
        const afterClient = clientDueDate && t.dueDate ? t.dueDate > clientDueDate : false;
        return (
          <button
            className="trow"
            key={t.id}
            onClick={() => onOpenTask(t)}
            {...hover(t.id, t.title)}
          >
            <span className="trow-main">
              <KindTag kind="task" size="row" />
              <span className="trow-title">{t.title}</span>
              {t.id === gatingTaskId ? (
                <span className="decides">DECIDES STATUS</span>
              ) : null}
              {/* A request still waiting for a PM (punch list F140). */}
              {isPendingRequest(t) ? <span className="chip requested">Requested</span> : null}
            </span>
            {/*
              * WHO HAS IT. This row showed a title, a status and a date and no
              * person at all, so the question these rows exist to answer --
              * where is this and who is it with -- took opening every task.
              *
              * It is the CURRENT STAGE's people, the same fact the Overview
              * calls "with whom", so the two screens cannot disagree. An
              * unstaffed stage reads "nobody", which is the true answer and
              * not the same as the task having no assignee.
              */}
            <span className="trow-who">
              {t.holders.length === 0
                ? "nobody"
                : /* Each name its own span, so yours can be marked without
                     marking the people beside it. */
                  t.holders.map((h, i) => (
                    <span key={h.key}>
                      {i > 0 ? ", " : ""}
                      <span className={h.key === meKey ? "mine-name" : undefined}>
                        {h.name}
                      </span>
                    </span>
                  ))}
            </span>
            <span className={`state ${t.status.toLowerCase().replace(" ", "_")}`}>
              {t.status}
            </span>
            <span className={`trow-due${overdue ? " is-late" : ""}`}>
              {t.dueDate ? mdy(t.dueDate) : "—"}
              {overdue ? <span className="overdue-tag">Overdue</span> : null}
              {afterClient ? (
                <span className="after-client" title="Due after the deliverable's client date">
                  ⚠ after client date
                </span>
              ) : null}
            </span>
          </button>
        );
      })}
    </>
  );
}

function NewDeliverable({
  fetcher,
  projectId,
  onCreated,
}: {
  fetcher: Fetcher;
  projectId: string;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [typeId, setTypeId] = useState("");
  const [clientDueDate, setClientDueDate] = useState("");
  const [types, setTypes] = useState<DeliverableTypeOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void api.deliverableTypes(fetcher).then(setTypes).catch(() => undefined);
  }, [fetcher]);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await api.createDeliverable(fetcher, projectId, {
        name,
        typeId: typeId || null,
        clientDueDate: clientDueDate || null,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="card" onSubmit={submit}>
      <h2>New deliverable</h2>
      <p className="muted" style={{ marginTop: -8 }}>
        What the client is owed. Tasks underneath it can move at their own pace.
      </p>
      {error ? <div className="error">{error}</div> : null}

      <div className="field">
        <label htmlFor="d-name">Name</label>
        <input
          id="d-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Phase I ESA — North Parcel"
          required
        />
      </div>

      <div className="field">
        <label htmlFor="d-type">Type — optional, used for reporting</label>
        <select id="d-type" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
          <option value="">Not categorised</option>
          {types.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="d-due">Due to client</label>
        <input
          id="d-due"
          type="date"
          {...PAST_DATE_BOX}
          value={clientDueDate}
          onChange={(e) => setClientDueDate(e.target.value)}
        />
      <DateHint />
      </div>

      <button className="primary" type="submit" disabled={saving}>
        {saving ? "Creating…" : "Create deliverable"}
      </button>
    </form>
  );
}

